import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { Category } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './categories.component.html',
  styleUrls: ['../../settings-shared.css', './categories.component.css'],
})
export class CategoriesComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');

  protected categoryForm: Omit<Category, 'id'> = {
    code: '',
    name: '',
    description: '',
    status: 'active',
  };

  protected readonly categories = this.settings.allCategories;

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Category): void {
    this.editingId.set(row.id);
    this.categoryForm = {
      code: row.code,
      name: row.name,
      description: row.description || '',
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.categoryForm.code || !this.categoryForm.name) {
      this.toastMessage.set('Code and Name are required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (editingId) {
      if (!this.settings.updateCategory(editingId, this.categoryForm)) {
        this.toastMessage.set(
          'Category update is not available via the API yet.',
        );
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        return;
      }
      this.toastMessage.set('Category updated successfully');
    } else {
      this.settings.addCategory(this.categoryForm);
      this.toastMessage.set('Category added successfully');
    }

    this.closeModal();
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  deleteRow(row: Category): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    if (!this.settings.deleteCategory(row.id)) {
      this.toastMessage.set(
        'Category delete is not available via the API yet.',
      );
    } else {
      this.toastMessage.set('Category deleted successfully');
    }
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  private resetForm(): void {
    this.categoryForm = {
      code: '',
      name: '',
      description: '',
      status: 'active',
    };
  }
}
